import { format } from 'date-fns'
import { enUS, es, ptBR } from 'date-fns/locale'
import { Calendar, CreditCard, Crown, Settings, Sparkles } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { BillingStatusBadge } from '@/components/billing/BillingStatusBadge'
import { Separator } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useBillingStatus } from '@/hooks/billing/useBillingStatus'
import { useStripeCheckout } from '@/hooks/billing/useSimpleStripeCheckout'
import { useStripePortal } from '@/hooks/billing/useStripePortal'
import { useStripePlans } from '@/hooks/usePostHogRemoteConfig'
import { formatCurrency } from '@/lib/utils'

export function BillingPage() {
  const { t, i18n } = useTranslation()
  const { data: billingStatus, isLoading } = useBillingStatus()
  const { data: plans, isLoading: isLoadingPrices, error: priceError } = useStripePlans()
  const { mutate: createCheckout, isPending: isCreatingCheckout } = useStripeCheckout()
  const { mutate: createPortalSession, isPending: isCreatingPortal } = useStripePortal()

  const getDateLocale = () => {
    const langMap = { pt: ptBR, es, en: enUS }
    return langMap[i18n.language as keyof typeof langMap] || enUS
  }

  const handleUpgrade = (plan: 'monthly' | 'annual') => {
    if (!plans) {
      console.error('Stripe plans not loaded')
      return
    }
    const priceId = plan === 'monthly' ? plans.monthly.priceId : plans.annual.priceId
    createCheckout({ priceId })
  }

  const handleManageSubscription = () => {
    createPortalSession({
      returnUrl: `${window.location.origin}/dashboard/billing`,
    })
  }

  // --- Render logic ---
  if (isLoading || isLoadingPrices) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-muted-foreground">{t('billing.loading')}</div>
      </div>
    )
  }

  if (priceError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-red-600 mb-2">Failed to load pricing configuration</div>
          <div className="text-sm text-muted-foreground">{priceError}</div>
        </div>
      </div>
    )
  }

  // --- Price calculations for display ---
  let monthlyPrice = ''
  let annualPrice = ''
  let annualSavingsAmount = ''
  let annualSavingsPercent = 0
  let annualPerMonthPrice = ''

  if (plans) {
    const localeForCurrency = i18n.language === 'pt' ? 'pt-BR' : 'en-US'
    monthlyPrice = formatCurrency(plans.monthly.amount, plans.currency, localeForCurrency)
    annualPrice = formatCurrency(plans.annual.amount, plans.currency, localeForCurrency)

    const totalAnnualCostIfMonthly = plans.monthly.amount * 12
    const savings = totalAnnualCostIfMonthly - plans.annual.amount
    if (savings > 0) {
      annualSavingsAmount = formatCurrency(savings, plans.currency, localeForCurrency)
      annualSavingsPercent = Math.round((savings / totalAnnualCostIfMonthly) * 100)
    }
    annualPerMonthPrice = formatCurrency(
      plans.annual.amount / 12,
      plans.currency,
      localeForCurrency
    )
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground">{t('billing.title')}</h1>
        <p className="mt-2 text-muted-foreground">{t('billing.subtitle')}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2 mb-8">
        <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              {t('billing.status.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">{t('billing.status.currentStatus')}</span>
              <BillingStatusBadge />
            </div>
            {billingStatus?.isExempt && (
              <>
                <Separator />
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    {t('billing.status.exemptAccess')}
                    {billingStatus.exemptReason &&
                      ` ${t('billing.status.exemptAs', { reason: billingStatus.exemptReason })}`}
                    .
                  </p>
                  {billingStatus.exemptExpiresAt && (
                    <p className="text-sm text-muted-foreground">
                      {t('billing.status.expiresOn', {
                        date: format(billingStatus.exemptExpiresAt, 'dd/MM/yyyy', {
                          locale: getDateLocale(),
                        }),
                      })}
                    </p>
                  )}
                </div>
              </>
            )}
            {billingStatus?.trialEndsAt && !billingStatus.subscription && (
              <>
                <Separator />
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">{t('billing.status.trialExpiresOn')}</span>
                  <span className="text-muted-foreground">
                    {format(billingStatus.trialEndsAt, 'dd/MM/yyyy', { locale: getDateLocale() })}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {billingStatus?.subscription && (
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('billing.subscription.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                <div className="flex justify-between">
                  <span className="font-medium">{t('billing.subscription.plan')}</span>
                  <span className="text-muted-foreground">
                    {billingStatus.subscription.plan === 'annual'
                      ? t('billing.subscription.annual')
                      : t('billing.subscription.monthly')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t('billing.subscription.nextBilling')}</span>
                  <span className="text-muted-foreground">
                    {format(billingStatus.subscription.currentPeriodEnd, 'dd/MM/yyyy', {
                      locale: getDateLocale(),
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t('billing.subscription.status')}</span>
                  <span className="text-muted-foreground">
                    {billingStatus.subscription.status === 'active'
                      ? t('billing.subscription.active')
                      : billingStatus.subscription.status}
                  </span>
                </div>
                {billingStatus.subscription.cancelAtPeriodEnd && (
                  <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                    <span className="font-medium text-orange-800">
                      {t('billing.subscription.cancelScheduled')}
                    </span>
                    <p className="text-sm text-orange-700 mt-1">
                      {t('billing.subscription.cancelDate', {
                        date: format(billingStatus.subscription.currentPeriodEnd, 'dd/MM/yyyy', {
                          locale: getDateLocale(),
                        }),
                      })}
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="space-y-6">
        {billingStatus?.subscription && (
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {t('billing.manage.title')}
              </CardTitle>
              <CardDescription>{t('billing.manage.description')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={handleManageSubscription}
                disabled={isCreatingPortal}
                className="w-full sm:w-auto"
              >
                {isCreatingPortal ? t('billing.manage.redirecting') : t('billing.manage.button')}
              </Button>
            </CardContent>
          </Card>
        )}

        {!billingStatus?.subscription && !billingStatus?.isExempt && (
          <Card className="border-0 shadow-sm hover:shadow-md transition-shadow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Crown className="w-5 h-5" />
                {t('billing.upgrade.title')}
              </CardTitle>
              <CardDescription>{t('billing.upgrade.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="relative flex flex-col p-6 bg-card rounded-lg border-0 hover:shadow-sm transition-shadow h-full">
                  <div className="flex-grow">
                    <div className="text-center pb-4">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-blue-500" />
                        <h3 className="text-xl font-semibold">
                          {t('billing.upgrade.monthly.title')}
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <div className="text-3xl font-bold">
                          {t('billing.upgrade.monthly.price', { price: monthlyPrice })}
                          <span className="text-base font-normal text-muted-foreground">
                            {t('billing.upgrade.monthly.period')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="w-full mt-auto"
                    variant="outline"
                    onClick={() => handleUpgrade('monthly')}
                    disabled={isCreatingCheckout || !plans}
                  >
                    {isCreatingCheckout
                      ? t('billing.upgrade.processing')
                      : t('billing.upgrade.monthly.cta')}
                  </Button>
                </div>

                <div className="relative flex flex-col p-6 bg-gradient-to-br from-purple-50/50 to-pink-50/50 rounded-lg border-0 hover:shadow-sm transition-shadow h-full">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 rounded-full text-sm font-medium shadow-lg">
                      {t('billing.upgrade.annual.badge')}
                    </div>
                  </div>
                  <div className="flex-grow">
                    <div className="text-center pb-4 pt-3">
                      <div className="flex items-center justify-center gap-2 mb-2">
                        <Crown className="w-5 h-5 text-purple-500" />
                        <h3 className="text-xl font-semibold">
                          {t('billing.upgrade.annual.title')}
                        </h3>
                      </div>
                      <div className="space-y-1">
                        <div className="text-3xl font-bold">
                          {t('billing.upgrade.annual.price', { price: annualPrice })}
                          <span className="text-base font-normal text-muted-foreground">
                            {t('billing.upgrade.annual.period')}
                          </span>
                        </div>
                        {annualSavingsAmount && (
                          <p className="text-sm text-green-600 font-medium">
                            {t('billing.upgrade.annual.savings', {
                              amount: annualSavingsAmount,
                              percent: annualSavingsPercent,
                            })}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          {t('billing.upgrade.annual.perMonth', { price: annualPerMonthPrice })}
                        </p>
                      </div>
                    </div>
                  </div>
                  <Button
                    className="w-full mt-auto bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
                    onClick={() => handleUpgrade('annual')}
                    disabled={isCreatingCheckout || !plans}
                  >
                    {isCreatingCheckout
                      ? t('billing.upgrade.processing')
                      : t('billing.upgrade.annual.cta')}
                  </Button>
                </div>
              </div>

              <div className="text-center space-y-2">
                <p className="text-sm text-muted-foreground">{t('billing.upgrade.benefits')}</p>
                <p className="text-xs text-muted-foreground">
                  {t('billing.upgrade.cancelAnytime')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
